import { oc } from '@orpc/contract';
import { z } from 'zod';
import { ValidationIssue } from './catalog.js';
import { OkResponse } from './common.js';
import { Code, DataType, FieldDefinition, FieldLevel, FieldUi, FieldValidation, GradeValue, ItemTypeField, ListItemType, LocalizedName, } from './meta.js';
import { EditableRefKind } from './refs.js';
/**
 * Администрирование метаданных (docs/PLAN.md, этап 3). Записи из seed и созданные в админке
 * различаются по origin; правка записи seed делает её customized — seed её больше не трогает.
 * Используемые записи не удаляются, а деактивируются.
 */
const Ownership = {
    origin: z.enum(['seed', 'admin']),
    customized: z.boolean(),
    active: z.boolean(),
};
const SortKey = z.string().max(16);
const Color = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'color');
const GradeCode = z.string().trim().min(1).max(32);
export const AdminItemType = z.object({
    code: Code,
    name: LocalizedName,
    icon: z.string().nullable(),
    titleTemplate: z.string(),
    defaultGradeScale: Code.nullable(),
    sortKey: z.string(),
    fields: z.array(ItemTypeField),
    ...Ownership,
    /** Записей каталога этого типа. */
    usage: z.number().int(),
});
export const AdminField = FieldDefinition.extend({
    ...Ownership,
    /** Встроенное поле (колонка или связь) — удалить нельзя. */
    core: z.boolean(),
    usage: z.object({ types: z.number().int(), records: z.number().int() }),
});
export const AdminEnumOption = z.object({
    setCode: Code,
    code: Code,
    name: LocalizedName,
    sortKey: z.string(),
    ...Ownership,
    usage: z.number().int(),
});
export const AdminStatus = z.object({
    code: Code,
    name: LocalizedName,
    color: z.string(),
    isOwned: z.boolean(),
    sortKey: z.string(),
    ...Ownership,
    usage: z.number().int(),
});
export const AdminGradeValue = GradeValue.omit({ active: true }).extend({
    ...Ownership,
    usage: z.number().int(),
});
export const AdminGradeScale = z.object({
    code: Code,
    name: LocalizedName,
    ...Ownership,
    usage: z.number().int(),
    values: z.array(AdminGradeValue),
});
export const AdminMeta = z.object({
    itemTypes: z.array(AdminItemType),
    fields: z.array(AdminField),
    enumOptions: z.array(AdminEnumOption),
    statuses: z.array(AdminStatus),
    gradeScales: z.array(AdminGradeScale),
});
export const adminMetaErrors = {
    NOT_FOUND: { status: 404, message: 'not_found' },
    CODE_TAKEN: { status: 409, message: 'code_taken' },
    IN_USE: {
        status: 409,
        message: 'in_use',
        data: z.object({ usage: z.number().int() }),
    },
    VALIDATION_FAILED: {
        status: 400,
        message: 'validation_failed',
        data: z.object({ issues: z.array(ValidationIssue) }),
    },
};
const route = (method, path) => oc.route({ method, path: `/admin/meta${path}` }).errors(adminMetaErrors);
export const FieldCreateInput = z.object({
    code: Code,
    name: LocalizedName,
    dataType: DataType,
    levels: z.array(FieldLevel).min(1),
    enumSet: Code.nullable().optional(),
    refKind: EditableRefKind.nullable().optional(),
    listOf: ListItemType.nullable().optional(),
    unit: z.string().trim().max(16).nullable().optional(),
    validation: FieldValidation.optional(),
    ui: FieldUi.omit({ widget: true }),
});
export const adminMetaContract = {
    get: oc.route({ method: 'GET', path: '/admin/meta' }).output(AdminMeta),
    types: {
        create: route('POST', '/types')
            .input(z.object({
            code: Code,
            name: LocalizedName,
            icon: z.string().max(32).nullable().optional(),
            titleTemplate: z.string().trim().min(1).max(300),
            defaultGradeScale: Code.nullable().optional(),
            sortKey: SortKey.optional(),
        }))
            .output(OkResponse),
        update: route('PATCH', '/types/{code}')
            .input(z.object({
            code: Code,
            name: LocalizedName.optional(),
            icon: z.string().max(32).nullable().optional(),
            titleTemplate: z.string().trim().min(1).max(300).optional(),
            defaultGradeScale: Code.nullable().optional(),
            sortKey: SortKey.optional(),
            active: z.boolean().optional(),
        }))
            .output(OkResponse),
        /** Состав полей типа целиком (порядок — sortKey, секции, обязательность). */
        setFields: route('POST', '/types/{code}/fields')
            .input(z.object({ code: Code, fields: z.array(ItemTypeField).max(200) }))
            .output(OkResponse),
        remove: route('DELETE', '/types/{code}')
            .input(z.object({ code: Code }))
            .output(OkResponse),
    },
    fields: {
        create: route('POST', '/fields').input(FieldCreateInput).output(OkResponse),
        update: route('PATCH', '/fields/{code}')
            .input(z.object({
            code: Code,
            name: LocalizedName.optional(),
            unit: z.string().trim().max(16).nullable().optional(),
            validation: FieldValidation.optional(),
            ui: FieldUi.omit({ widget: true }).optional(),
            active: z.boolean().optional(),
        }))
            .output(OkResponse),
        remove: route('DELETE', '/fields/{code}')
            .input(z.object({ code: Code }))
            .output(OkResponse),
    },
    enumOptions: {
        create: route('POST', '/enums/{setCode}')
            .input(z.object({ setCode: Code, code: Code, name: LocalizedName, sortKey: SortKey.optional() }))
            .output(OkResponse),
        update: route('PATCH', '/enums/{setCode}/{code}')
            .input(z.object({
            setCode: Code,
            code: Code,
            name: LocalizedName.optional(),
            sortKey: SortKey.optional(),
            active: z.boolean().optional(),
        }))
            .output(OkResponse),
        remove: route('DELETE', '/enums/{setCode}/{code}')
            .input(z.object({ setCode: Code, code: Code }))
            .output(OkResponse),
    },
    statuses: {
        create: route('POST', '/statuses')
            .input(z.object({
            code: Code,
            name: LocalizedName,
            color: Color,
            isOwned: z.boolean(),
            sortKey: SortKey.optional(),
        }))
            .output(OkResponse),
        update: route('PATCH', '/statuses/{code}')
            .input(z.object({
            code: Code,
            name: LocalizedName.optional(),
            color: Color.optional(),
            isOwned: z.boolean().optional(),
            sortKey: SortKey.optional(),
            active: z.boolean().optional(),
        }))
            .output(OkResponse),
        remove: route('DELETE', '/statuses/{code}')
            .input(z.object({ code: Code }))
            .output(OkResponse),
    },
    gradeScales: {
        create: route('POST', '/grades')
            .input(z.object({ code: Code, name: LocalizedName }))
            .output(OkResponse),
        update: route('PATCH', '/grades/{code}')
            .input(z.object({ code: Code, name: LocalizedName.optional(), active: z.boolean().optional() }))
            .output(OkResponse),
        createValue: route('POST', '/grades/{scale}/values')
            .input(z.object({
            scale: Code,
            code: GradeCode,
            name: LocalizedName,
            rank: z.number().int().min(0).max(100),
        }))
            .output(OkResponse),
        updateValue: route('PATCH', '/grades/{scale}/values/{code}')
            .input(z.object({
            scale: Code,
            code: GradeCode,
            name: LocalizedName.optional(),
            rank: z.number().int().min(0).max(100).optional(),
            active: z.boolean().optional(),
        }))
            .output(OkResponse),
        removeValue: route('DELETE', '/grades/{scale}/values/{code}')
            .input(z.object({ scale: Code, code: GradeCode }))
            .output(OkResponse),
    },
};
//# sourceMappingURL=admin-meta.js.map